import { API_BASE } from "./config/api";
// ✅ ManageFamilyGroup_Step1.js — Single Save button + DEBUG alerts (payload & response)
// APIs:
//  - GET  /api/get_family_groups.php?arn_no=ARN
//  - GET  /api/get_family_group_details.php?arn_no=ARN&master_pan=PAN
//  - GET  /api/get_available_childforfamilygroups.php?arn_no=ARN
//  - POST /api/save_family_groups.php   (arn_no, master_pan, group_name, members[{pan,is_allowed}])

import React, { useEffect, useMemo, useState } from "react";
import {
  Button, Card, Empty, Form, Grid, Input, Modal, Space, Table, Typography, Tag, message, Transfer, Checkbox
} from "antd";
import { ArrowRightOutlined, UserAddOutlined, SearchOutlined, TeamOutlined, SaveOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title } = Typography;
const { useBreakpoint } = Grid;


const getArn = () => localStorage.getItem("arn_number");

// ---- helpers ----
// 👉 success के बाद पूरा UI साफ़ करने के लिए
const toTitleCase = (s = "") =>
  String(s)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const cleanName = (s = "") =>
  toTitleCase(
    String(s)
      .replace(/[^A-Za-z.\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  );

const norm = (v) => (v ?? "").toString().trim().toLowerCase();

function getModalDims(screens) {
  if (!screens.sm) return { width: "94vw", bodyStyle: { maxHeight: "70vh", overflowY: "auto", padding: 12 }, centered: true };
  if (screens.sm && !screens.md) return { width: 560, bodyStyle: { maxHeight: "72vh", overflowY: "auto", padding: 12 }, centered: true };
  return { width: 720, bodyStyle: { maxHeight: "75vh", overflowY: "auto", padding: 12 }, centered: true };
}

export default function ManageFamilyGroup_Step1() {
  const screens = useBreakpoint();
  const modalDims = getModalDims(screens);
  const isMobile = !screens.md;

  const [loading, setLoading] = useState(false);

  // LEFT list (heads)
  const [candidates, setCandidates] = useState([]);

  // Right panel state
  const [q, setQ] = useState("");
  const [selectedHead, setSelectedHead] = useState(null); // {pan, invester_name}
  const [groupName, setGroupName] = useState("");
  const [members, setMembers] = useState([]); // [{pan, invester_name, is_allowed:boolean}]
  const [isSaving, setIsSaving] = useState(false);

const resetForm = () => {
  setSelectedHead(null);
  setGroupName("");
  setMembers([]);
  setMemberModalOpen(false);
  setTransferData([]);
  setTransferTargetKeys([]);
  setQ(""); // search भी clear
};

  // Dual-list modal
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [transferData, setTransferData] = useState([]);
  const [transferTargetKeys, setTransferTargetKeys] = useState([]);
  const [transferLoading, setTransferLoading] = useState(false);


  const loadCandidates = async () => {
    const arn = getArn();
    if (!arn) return;
    try {
      setLoading(true);
      const candRes = await axios.get(
        `${API_BASE}/get_family_groups.php?arn_no=${encodeURIComponent(arn)}`,
        { timeout: 20000 }
      );
      const raw = Array.isArray(candRes?.data?.candidates) ? candRes.data.candidates : [];
      const normalized = raw.map((r) => ({
        pan: String(r.pan || "").toUpperCase(),
        invester_name: cleanName(r.invester_name || ""),
        key: String(r.pan || "").toUpperCase(),
      }));
      setCandidates(normalized);
    } catch (e) {
      message.error("Failed to load group heads.");
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const arn = getArn();
    if (!arn) {
      window.alert("Session information not found. कृपया दोबारा login करें।");
      message.error("ARN not found in localStorage. Please login again.");
      return;
    }
    loadCandidates();
  }, []);

  const headCandidates = useMemo(() => {
    if (!q) return candidates;
    const s = norm(q);
    return candidates.filter(
      (row) => norm(row.invester_name).includes(s) || norm(row.pan).includes(s)
    );
  }, [candidates, q]);

  const handleSelectHead = async (rec) => {
    const arn = getArn();
    if (!arn) {
      window.alert("Session information missing. Please login again.");
      message.error("ARN missing.");
      return;
    }
    setSelectedHead(rec);
    setMembers([]);
    const defaultGroupName = `${rec.invester_name} Family`;
    setGroupName(defaultGroupName);

    try {
      const url = `${API_BASE}/get_family_group_details.php?arn_no=${encodeURIComponent(
        arn
      )}&master_pan=${encodeURIComponent(rec.pan)}`;
      const res = await axios.get(url, { timeout: 20000 });

      const data = res?.data || {};
      if (data?.group_name && String(data.group_name).trim() !== "") {
        setGroupName(String(data.group_name).trim());
      }
      const arr = Array.isArray(data?.members) ? data.members : [];
      const mapped = arr.map((m) => {
        const pan = String(m.child_pan || "").toUpperCase();
        const nm = cleanName(m.child_name || "");
        const allowed = Number(m.is_allowed ?? 0) === 1;
        return { key: pan, pan, invester_name: nm || "—", is_allowed: allowed };
      });
      setMembers(mapped);
    } catch (e) {
      message.warning("No saved grouping found. Using default group name.");
      setMembers([]);
    }
  };

  const headCols = [
    { title: "Investor Name", dataIndex: "invester_name", key: "invester_name" },
    { title: "PAN", dataIndex: "pan", key: "pan", width: 120 },
    {
      title: "",
      key: "act",
      width: 56,
      render: (_, rec) => (
        <Button
          type="text"
          icon={<ArrowRightOutlined />}
          aria-label="Use as Group Head"
          onClick={() => handleSelectHead(rec)}
        />
      ),
    },
  ];

  const openMemberModal = async () => {
    const arn = getArn();
    if (!arn) return;
    if (!selectedHead) {
      message.warning("Select a group head first.");
      return;
    }
    setTransferLoading(true);
    setMemberModalOpen(true);

    try {
      const availRes = await axios.get(
        `${API_BASE}/get_available_childforfamilygroups.php?arn_no=${encodeURIComponent(arn)}`,
        { timeout: 20000 }
      );
      const available = Array.isArray(availRes?.data?.candidates)
        ? availRes.data.candidates.map((c) => ({
            key: String(c.pan || "").toUpperCase(),
            pan: String(c.pan || "").toUpperCase(),
            invester_name: cleanName(c.invester_name || ""),
            is_allowed: false, // default unchecked for new
          }))
        : [];

      const currentRight = (members || []).map((m) => ({
        key: String(m.pan || "").toUpperCase(),
        pan: String(m.pan || "").toUpperCase(),
        invester_name: cleanName(m.invester_name || ""),
        is_allowed: !!m.is_allowed,
      }));

      const headPan = selectedHead?.pan ? String(selectedHead.pan).toUpperCase() : null;
      const filteredAvailable = available.filter((x) => x.pan !== headPan);

      const mapByPan = new Map();
      [...filteredAvailable, ...currentRight].forEach((item) => mapByPan.set(item.pan, item));
      const union = Array.from(mapByPan.values()).sort((a, b) =>
        a.invester_name.localeCompare(b.invester_name)
      );

      setTransferData(union);
      setTransferTargetKeys(currentRight.map((x) => x.key));
    } catch (e) {
      message.error("Failed to load available members.");
      const currentRight = (members || []).map((m) => ({
        key: String(m.pan || "").toUpperCase(),
        pan: String(m.pan || "").toUpperCase(),
        invester_name: cleanName(m.invester_name || ""),
        is_allowed: !!m.is_allowed,
      }));
      setTransferData(currentRight);
      setTransferTargetKeys(currentRight.map((x) => x.key));
    } finally {
      setTransferLoading(false);
    }
  };

  const filterOption = (inputValue, item) =>
    norm(item.invester_name).includes(norm(inputValue)) ||
    norm(item.pan).includes(norm(inputValue));

  const applyTransferSelection = () => {
    const mapByKey = new Map(transferData.map((d) => [d.key, d]));
    const nextMembers = transferTargetKeys
      .map((k) => mapByKey.get(k))
      .filter(Boolean)
      .map((d) => ({
        key: d.pan,
        pan: d.pan,
        invester_name: d.invester_name,
        is_allowed: !!d.is_allowed,
      }));
    setMembers(nextMembers);
    setMemberModalOpen(false);
    message.success("Members updated.");
  };

  const toggleAllowed = (pan, checked) => {
    setMembers((prev) => prev.map((m) => (m.pan === pan ? { ...m, is_allowed: checked } : m)));
  };

  // ---- SAVE / UPDATE GROUP (ONLY bottom button) ----
// ... बाकी imports/कोड जैसा है वैसा रहने दें

const saveGroup = async () => {
  const arn = getArn();
  if (!arn) {
    window.alert("Session information not found. कृपया दोबारा login करें।");
    return;
  }
  if (!selectedHead) {
    window.alert("कृपया पहले बाएँ list से Group Head चुनें।");
    return;
  }

  const payload = {
    arn_no: arn,
    master_pan: String(selectedHead.pan).toUpperCase(),
    master_name: selectedHead.invester_name || "",     // ✅ नया फील्ड
    group_name: (groupName || "").trim(),
    members: (members || []).map((m) => ({
      pan: String(m.pan).toUpperCase(),
      child_name: m.invester_name || "",              // ✅ नया फील्ड
      is_allowed: !!m.is_allowed,
    })),
  };

  // DEBUG alert
  //window.alert("[DEBUG] Saving payload:\n" + JSON.stringify(payload, null, 2));

  try {
    setIsSaving(true);
    const res = await axios.post(
      `${API_BASE}/save_family_groups.php`,
      payload,
      {
        headers: { "Content-Type": "application/json" },
        timeout: 30000,
      }
    );

    //window.alert("[DEBUG] API response:\n" + JSON.stringify(res?.data ?? {}, null, 2));

    if (res?.data?.success) {
      const savedCount = res?.data?.saved_count ?? 0;
	
    	window.alert("Group Created/Updated Successfully" );
    	 await loadCandidates();

  		// ✅ पूरा फॉर्म blank
  		resetForm();
      // चाहें तो यहाँ message.success(...) भी रख सकते हैं
//      await Promise.all([handleSelectHead(selectedHead), loadCandidates()]);
    } else {
      window.alert("[ERROR] Save failed:\n" + (res?.data?.message || "Unknown error"));
    }
  } catch (e) {
    // Axios "Network Error" आमतौर पर CORS/SSL/Timeout होता है
    const status = e?.response?.status;
    const text = status ? `HTTP ${status}` : (e?.message || "Network Error");
    window.alert("[ERROR] Save failed:\n" + text);
  } finally {
    setIsSaving(false);
  }
};

  const listStyle = {
    width: isMobile ? 140 : 260,
    height: isMobile ? 280 : 360,
  };

  return (
    <div style={{ padding: isMobile ? 12 : 20 }}>
<div style={{ width: "100%", display: "flex", justifyContent: "center", marginBottom: 12 }}>
  <Space align="center" size={8}>
    <TeamOutlined />
    <Title level={4} style={{ margin: 0, textDecoration: "underline" }}>
      Manage Family Groups
    </Title>
  </Space>
</div>
        {/* ⛔️ Top-right Save button removed */}
        <div />

      <Space direction={isMobile ? "vertical" : "horizontal"} size={12} style={{ display: "flex" }}>
        {/* Left: Group Head List */}
        <Card style={{ flex: isMobile ? "1 1 100%" : "0 0 520px" }} bodyStyle={{ padding: 12 }}>
          <Space style={{ width: "100%", marginBottom: 8 }}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search investor name or PAN"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </Space>

          <Table
            size="small"
            columns={headCols}
            dataSource={headCandidates}
            loading={loading}
            pagination={{ pageSize: 8, showSizeChanger: false }}
            rowKey="pan"
          />
          {!loading && !headCandidates.length && (
            <Empty description="No candidates (filtered or none available)" />
          )}
        </Card>

        {/* Right: Selected Head + Members */}
        <Card style={{ flex: "1 1 auto" }} bodyStyle={{ padding: 12 }}>
          <Form layout="vertical">
            <Form.Item label="Group Name">
              <Input
                placeholder="Enter group name (e.g., Sharma Family)"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                maxLength={60}
              />
            </Form.Item>

            <Form.Item label="Group Head Name">
              <Input value={selectedHead?.invester_name || ""} readOnly placeholder="Select from left list" />
            </Form.Item>
            <Form.Item label="Group Head PAN">
              <Input value={selectedHead?.pan || ""} readOnly />
            </Form.Item>

            <Space wrap align="center" style={{ marginBottom: 4 }}>
              {selectedHead && <Tag color="blue">Head: {selectedHead.pan}</Tag>}
              {groupName && <Tag color="gold">Group: {groupName}</Tag>}
            </Space>
          </Form>

          {selectedHead && (
            <>
              <Title level={5} style={{ marginTop: 8 }}>Group Members</Title>
              <Table
                size="small"
                columns={[
                  { title: "Investor Name", dataIndex: "invester_name", key: "invester_name" },
                  { title: "PAN", dataIndex: "pan", key: "pan", width: 140 },
                  {
                    title: "Allowed to View Group Portfolio",
                    dataIndex: "is_allowed",
                    key: "is_allowed",
                    width: 260,
                    render: (v, record) => (
                      <Checkbox
                        checked={!!v}
                        onChange={(e) => toggleAllowed(record.pan, e.target.checked)}
                      />
                    ),
                  },
                ]}
                dataSource={members}
                pagination={{ pageSize: 6, showSizeChanger: false }}
                locale={{ emptyText: <Empty description="No members yet" /> }}
                rowKey="pan"
              />

              <Space style={{ marginTop: 8 }}>
                <Button icon={<UserAddOutlined />} onClick={openMemberModal} disabled={!selectedHead}>
                  Add more members
                </Button>

                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={saveGroup}
                  loading={isSaving}
                  disabled={!selectedHead}
                >
                  Save / Update Group
                </Button>
              </Space>
            </>
          )}
        </Card>
      </Space>

      {/* Dual-list Member Selection Popup */}
      <Modal
        title="Add/Remove Group Members"
        open={memberModalOpen}
        onCancel={() => setMemberModalOpen(false)}
        footer={null}
        destroyOnClose
        maskClosable={false}
        keyboard={false}
        {...modalDims}
      >
        <Transfer
          dataSource={transferData}
          targetKeys={transferTargetKeys}
          onChange={setTransferTargetKeys}
          titles={["Available members", "Added members"]}
          showSearch
          filterOption={filterOption}
          listStyle={listStyle}
          render={(item) => `${item.invester_name} (${item.pan})`}
          oneWay={false}
          disabled={transferLoading}
          pagination
        />

        <Space style={{ justifyContent: "flex-end", width: "100%", marginTop: 12 }}>
          <Button onClick={() => setMemberModalOpen(false)}>Cancel</Button>
          <Button type="primary" onClick={applyTransferSelection}>Done</Button>
        </Space>
      </Modal>
    </div>
  );
}
